/**
 * [rev17 Phase A1] TransactionFieldRow
 *
 * Shared row primitive owned by both the Detail (read) and the Edit (form)
 * modals. The same field metadata (icon, label, required/optional marker,
 * tooltip, canonical order, custom renderers) is reused verbatim, and the row
 * anatomy (icon / label / value cells + value-area validation message) is
 * identical across both modes so the rev17 cross-modal tests can measure parity
 * between Detail, Edit, and Add. Both modes therefore share the same three grid
 * tracks; Detail's optional 44px edit shortcut renders inside the label cell,
 * immediately beside the field title, instead of occupying a far-right column.
 *
 * D2 row contract (plan §3.2): `min-w-0` on every cell that can shrink;
 * `break-words` on read-mode label/value cells; no fixed `min-w-[110px]` label
 * reservation; edit-mode metadata uses deliberate non-breaking tokens; the
 * validation message spans the value area below the control instead of becoming
 * a fourth horizontal column; custom renderers receive the same width/min-height
 * contract as default inputs.
 *
 * Rev19 P1/P2 alignment contract: edit rows align icon, label, and control on
 * the shared text baseline (`items-baseline`, no `pt-*` nudges, no FormItem
 * `space-y-*` margins), and the edit label renders its title plus
 * required/optional/tooltip metadata as one inline row.
 *
 * D5 token contract (plan §3.5): `text-foreground`, `text-muted-foreground`,
 * `border-border`, `text-primary` — no `text-slate-*` or `border-slate-*` literals.
 *
 * @see docs/superpowers/plans/2026-08-16-rev17-modal-redesign-plan.md (A1, D2, D5)
 */
import * as React from 'react';
import { HelpCircle, Pencil, type LucideIcon } from 'lucide-react';
import { createPortal } from 'react-dom';
import type {
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  UseFormReturn,
} from 'react-hook-form';

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  OptionalTag,
  RequiredAsterisk,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import type { TransactionFormData } from './TransactionForm';

/** Render mode for the field row. 'read' is Detail; 'edit' is Edit/Add. */
export type FieldMode = 'read' | 'edit';

/**
 * Edit-mode renderer override signature. Mirrors react-hook-form's Controller
 * render-prop shape so callers can plug in Select, CurrencySelect, EmojiPicker,
 * TagInput, etc. without losing form integration.
 */
export type EditRendererArgs = {
  field: ControllerRenderProps<TransactionFormData, FieldPath<TransactionFormData>>;
  fieldState: ControllerFieldState;
};

/** Read-mode renderer override signature. Receives the current field value. */
export type ReadRenderer<T> = (value: T) => React.ReactNode;

/**
 * Static metadata for a single transaction field. Both the canonical field
 * registry (PRIMARY_FIELDS / SECONDARY_FIELDS in fieldSpec.ts, Phase A-2) and
 * ad-hoc per-row configuration can describe their fields with this shape.
 *
 * The `key` must match a path in TransactionFormData (e.g. 'amount', 'tags',
 * `items.0.description`). The runtime cast to FieldPath<TransactionFormData>
 * inside the component asserts that constraint against the supplied `form`.
 */
export interface FieldSpec<T = unknown> {
  /** Stable key matching the TransactionFormData path. */
  key: string;
  /** Label shown to the user. Sentence case, kept short. */
  label: string;
  /** Lucide icon rendered left of the label in both read and edit modes. */
  icon?: LucideIcon;
  /** Marks the field required. Drives RequiredAsterisk in edit mode. */
  required?: boolean;
  /** Marks the field optional. Drives OptionalTag in edit mode. */
  optional?: boolean;
  /** Helper text shown via the FieldTooltip icon in edit mode. */
  tooltip?: string;
  /** Order in the canonical field sequence. Lower renders earlier. */
  order: number;
  /** Read-only options metadata; preserved for parity with fieldSpec.ts. */
  options?: Array<{ value: string; label: string; emoji?: string }>;
  /** Override the default read view (e.g. date formatting, currency). */
  readRenderer?: ReadRenderer<T>;
  /** Override the default edit view (e.g. Select, CurrencySelect, EmojiPicker). */
  editRenderer?: (args: EditRendererArgs) => React.ReactNode;
}

export interface TransactionFieldRowProps<T = unknown> {
  field: FieldSpec<T>;
  mode: FieldMode;
  /** Current value. Read mode passes the transaction value; edit mode is unused (form owns it). */
  value: T;
  /** Optional change handler. No-op in read mode. */
  onChange?: (value: T) => void;
  /** Form integration. Required when mode === 'edit'. */
  form?: UseFormReturn<TransactionFormData>;
  /** Optional 'Edit' shortcut target for the Detail modal (Option Y pattern). */
  onEditShortcut?: () => void;
  /**
   * Optional read-mode-only adornment rendered immediately after the
   * existing pencil shortcut inside the read-mode label cell. The Detail
   * Icon row (R26-1) supplies the selected emoji through this slot as a
   * non-interactive `<span>` so the value is not duplicated in the generic
   * value cell. Ignored in edit mode.
   */
  readLabelAdornment?: React.ReactNode;
}

/** Returns true when the value should render as the empty-state glyph. */
function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.length === 0) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/** Safe stringification for unknown values; never throws, never reads inner members. */
function renderFallbackValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }
  return '';
}

/**
 * Narrow a Controller field value to the subset that an HTML <input> accepts.
 * The Controller union includes boolean, Date, arrays of objects, and object literals;
 * the DOM input contract only allows string | number | readonly string[]. Anything outside that
 * contract coerces to '' so the default Input boundary remains type-safe without altering
 * FieldSpec, custom renderers, or the form schema. Custom `editRenderer` overrides are
 * unaffected because this helper is only used by the `<Input>` default fallback.
 */
function toInputCompatibleValue(value: unknown): string | number | readonly string[] {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value;
  if (Array.isArray(value) && value.every((item): item is string => typeof item === 'string')) {
    return value as readonly string[];
  }
  return '';
}

function FieldHelperTooltip({ text }: { text: string }): React.ReactElement {
  const tooltipId = React.useId();
  const [open, setOpen] = React.useState(false);
  const touchOpenPendingRef = React.useRef(false);
  const [isCompactViewport, setIsCompactViewport] = React.useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  ));

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = () => setIsCompactViewport(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label="Show field help"
            aria-describedby={tooltipId}
            aria-expanded={open}
            onPointerDownCapture={(event) => {
              if (event.pointerType === 'touch' && !open) {
                touchOpenPendingRef.current = true;
                setOpen(true);
              }
            }}
            onClickCapture={(event) => {
              if (touchOpenPendingRef.current) {
                touchOpenPendingRef.current = false;
                event.preventDefault();
                event.stopPropagation();
              }
            }}
            className="-m-2 inline-flex h-11 w-11 min-h-[44px] min-w-[44px] items-center justify-center rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <HelpCircle className="h-3.5 w-3.5 cursor-help" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          id={tooltipId}
          side="right"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          className={isCompactViewport ? 'sr-only' : 'max-w-[240px] border-border bg-popover text-xs leading-relaxed shadow-lg'}
        >
          {text}
        </TooltipContent>
      </Tooltip>
      {isCompactViewport && open && typeof document !== 'undefined'
        ? createPortal(
            <div
              role="tooltip"
              aria-hidden="true"
              className="fixed bottom-[76px] left-3 right-3 z-[100] rounded-lg border border-border bg-popover px-3 py-2 text-xs leading-relaxed text-popover-foreground shadow-lg"
            >
              {text}
            </div>,
            document.body,
          )
        : null}
    </TooltipProvider>
  );
}

export function TransactionFieldRow(props: TransactionFieldRowProps): React.ReactElement {
  const { field, mode, value, form, onEditShortcut, readLabelAdornment } = props;

  // ── READ MODE ──────────────────────────────────────────────────────
  if (mode === 'read') {
    const Icon = field.icon;
    return (
<div
data-testid={`txn-field-row-${field.key}`}
        className="grid grid-cols-[28px_minmax(100px,1fr)_minmax(0,2fr)] gap-x-3 gap-y-2 items-center py-4 border-b border-border last:border-b-0 sm:grid-cols-[28px_100px_minmax(0,1fr)]"
>
<div
data-testid={`txn-field-cell-icon-${field.key}`}
className="shrink-0 flex justify-start pt-0.5"
>
{Icon ? (
<Icon
className="h-[18px] w-[18px] text-muted-foreground"
aria-hidden="true"
/>
) : (
<span className="block h-[18px] w-[18px]" aria-hidden="true" />
)}
</div>
<div
data-testid={`txn-field-cell-label-${field.key}`}
className="min-w-0 flex items-center gap-1 break-words text-xs font-semibold text-foreground/80"
>
            <span data-testid={`txn-field-label-title-${field.key}`} className="min-w-0 whitespace-nowrap">{field.label}</span>
{onEditShortcut ? (
<button
type="button"
onClick={onEditShortcut}
className="ml-auto h-11 w-11 min-h-[44px] min-w-[44px] shrink-0 inline-flex items-center justify-center text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm"
data-testid={`txn-field-edit-link-${field.key}`}
aria-label={`Edit ${field.label}`}
>
<Pencil className="h-4 w-4" aria-hidden="true" />
<span className="sr-only">Edit</span>
</button>
) : null}
{readLabelAdornment ? (
  <span
    data-testid={`txn-field-read-adornment-${field.key}`}
    className="inline-flex items-center text-base text-foreground whitespace-nowrap"
  >
    {readLabelAdornment}
  </span>
) : null}
</div>
<div
data-testid={`txn-field-cell-value-${field.key}`}
className="min-w-0 break-words text-sm font-medium text-foreground"
>
{field.readRenderer
? field.readRenderer(value)
: isEmptyValue(value)
? '—'
: renderFallbackValue(value)}
</div>
</div>
    );
  }

  // ── EDIT MODE ──────────────────────────────────────────────────────
  if (!form) {
    throw new Error(
      `TransactionFieldRow: 'form' is required when mode === 'edit' (field key: ${field.key})`,
    );
  }

  const Icon = field.icon;

  return (
    <FormField
      control={form.control}
      name={field.key as FieldPath<TransactionFormData>}
      render={({ field: rf, fieldState }) => (
<FormItem
data-testid={`txn-field-row-${field.key}`}
          className="mb-2 grid grid-cols-[28px_minmax(100px,1fr)_minmax(0,2fr)] max-[374px]:grid-cols-[28px_minmax(0,1fr)] gap-x-3 gap-y-2 space-y-0 py-4 border-b border-border last:border-b-0 items-center sm:grid-cols-[28px_100px_minmax(0,1fr)]"

>
<div
data-testid={`txn-field-cell-icon-${field.key}`}
className="shrink-0 flex items-center justify-start min-h-[20px]"


>
{Icon ? (
<Icon
className="h-[20px] w-[20px] text-muted-foreground"

aria-hidden="true"
/>
) : (
<span className="block h-[20px] w-[20px]" aria-hidden="true" />

)}
</div>
<div
data-testid={`txn-field-cell-label-${field.key}`}
className="min-w-0 break-normal"
>
<FormLabel className="m-0 flex flex-wrap items-baseline gap-x-1 gap-y-0 text-xs font-semibold text-foreground/80">
<span
data-testid={`txn-field-label-title-${field.key}`}
className="min-w-0 inline-flex items-center min-h-[20px] whitespace-nowrap"

>
{field.label}
</span>
{field.required || field.optional || field.tooltip ? (
<span
data-testid={`txn-field-label-meta-${field.key}`}
className="flex items-center gap-0.5 whitespace-nowrap text-[10px] font-normal leading-3 text-muted-foreground [&>span]:ml-0 [&>span]:text-[10px] [&>svg]:ml-0 max-[374px]:gap-0 max-[374px]:text-[9px] max-[374px]:[&>span]:text-[9px] max-[374px]:[&>svg]:h-3 max-[374px]:[&>svg]:w-3"
>
{field.required ? <RequiredAsterisk /> : null}
{field.optional ? <OptionalTag /> : null}
{field.tooltip ? (
  <span
    data-testid={`txn-field-tooltip-${field.key}`}
    aria-label={field.tooltip}
    className="inline-flex items-center"
  >
    <FieldHelperTooltip text={field.tooltip} />
  </span>
) : null}
</span>
) : null}
</FormLabel>
</div>
<div
data-testid={`txn-field-cell-value-${field.key}`}
className="min-w-0 space-y-1 max-[374px]:col-start-2"
>
<FormControl className="w-full min-w-0">
{field.editRenderer ? (
field.editRenderer({ field: rf, fieldState })
) : (
<Input
{...rf}
value={toInputCompatibleValue(rf.value)}
data-testid={`txn-field-input-${field.key}`}
className="min-h-[44px] w-full min-w-0"
/>
)}
</FormControl>
<FormMessage data-testid={`txn-field-error-${field.key}`} />
</div>
</FormItem>
      )}
    />
  );
}
